// Menu — top-level screen router
// Manages transitions between: server browser, host form, and playing.

import type { ServerInfo } from "../../net/server.js";
import { createServer, type CreateServerResult } from "../../engine/launcher.js";
import { serverWsUrl } from "../../engine/launcher.js";
import { ServerBrowser } from "./browser.js";
import { HostServerUI } from "./host.js";
import { recordJoin } from "./history.js";

export type MenuCallbacks = {
  /** Called when the player wants to join a server (by click or direct URL). */
  onJoin: (wsUrl: string) => void;
};

export class Menu {
  private browser: ServerBrowser | null = null;
  private host: HostServerUI | null = null;
  private relayHttp: string;
  private relayWs: string;
  private callbacks: MenuCallbacks;

  constructor(relayHttp: string, relayWs: string, callbacks: MenuCallbacks) {
    this.relayHttp = relayHttp;
    this.relayWs = relayWs;
    this.callbacks = callbacks;
  }

  /** Show the server browser (home screen). */
  showBrowser(error?: string, mapCode?: string): void {
    this.unmountAll();
    this.browser = new ServerBrowser(this.relayHttp, {
      onJoin: (server: ServerInfo, password?: string) => {
        recordJoin(server.name, server.mapName);
        this.unmountAll();
        let wsUrl = serverWsUrl(this.relayWs, server.id);
        if (password) wsUrl += `?password=${encodeURIComponent(password)}`;
        this.callbacks.onJoin(wsUrl);
      },
      onHost: () => this.showHost(),
      onHostMap: (code: string) => this.showHost(code),
      onQuickPlay: (servers: ServerInfo[]) => this.quickPlay(servers),
    });
    this.browser.mount(error, mapCode);
  }

  /** Show the host server form as a modal over the browser. */
  showHost(mapCode?: string): void {
    // Unmount previous host modal if open, but keep browser
    this.host?.unmount();
    this.host = new HostServerUI(this.relayHttp, {
      onCreated: (result: CreateServerResult) => {
        recordJoin(result.server.name, result.server.mapName);
        this.unmountAll();
        this.callbacks.onJoin(result.wsUrl);
      },
      onBack: () => {
        this.host?.unmount();
        this.host = null;
      },
    });
    this.host.mount(mapCode);
  }

  /** Quick Play — join best server or create one. */
  private async quickPlay(servers: ServerInfo[]): Promise<void> {
    // Find the best joinable server: has players, not full, sorted by most players
    const joinable = servers
      .filter(s => s.playerCount < s.maxPlayers && !s.hasPassword)
      .sort((a, b) => b.playerCount - a.playerCount);

    if (joinable.length > 0) {
      recordJoin(joinable[0].name, joinable[0].mapName);
      this.unmountAll();
      const wsUrl = serverWsUrl(this.relayWs, joinable[0].id);
      this.callbacks.onJoin(wsUrl);
      return;
    }

    // No servers available — create a default one
    try {
      const result = await createServer(this.relayHttp, {
        name: "Quick Play",
        maxPlayers: 8,
        mapSource: { type: "builtin", name: "surf" },
        gameConfig: { mode: "surf", preset: "classic" },
      });
      recordJoin(result.server.name, result.server.mapName);
      this.unmountAll();
      this.callbacks.onJoin(result.wsUrl);
    } catch (err) {
      console.error("Quick Play failed:", err);
    }
  }

  /** Remove all UI (transition to playing state). */
  unmountAll(): void {
    this.browser?.unmount();
    this.browser = null;
    this.host?.unmount();
    this.host = null;
  }
}
